import { createTheme } from '@mui/material/styles';

/**
 * PRAY4ALL DESIGN SYSTEM
 * Elegant & Spiritual Theme with WCAG AA compliance
 * Version: 3.0 - Warm & Contemplative
 *
 * Design Philosophy:
 * - Warm, welcoming tones that evoke peace and spirituality
 * - Elegant typography with Playfair Display for headings
 * - Soft shadows and generous spacing for a calm atmosphere
 * - Gold accents for spiritual significance
 */

// ==================== ELEGANT COLOR PALETTE ====================
const colors = {
  primary: {
    main: '#2C5282',      // Deep elegant blue
    light: '#4A7BB7',
    dark: '#1A365D',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#D4A574',      // Warm gold accent
    light: '#E5C9A8',
    dark: '#B8844D',
    contrastText: '#1A365D',
  },
  success: {
    main: '#68D391',      // Sage green
    light: '#9AE6B4',
    dark: '#48BB78',
    contrastText: '#1A365D',
  },
  error: {
    main: '#E53E3E',
    light: '#FC8181',
    dark: '#C53030',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#DD6B20',
    light: '#F6AD55',     // Peach orange
    dark: '#C05621',
    contrastText: '#ffffff',
  },
  info: {
    main: '#63B3ED',      // Sky blue
    light: '#90CDF4',
    dark: '#4299E1',
    contrastText: '#1A365D',
  },
  background: {
    default: '#FAFAF8',   // Warm white
    paper: '#FFFFFF',
    warm: '#F7F5F0',      // Warm cream for cards
  },
  text: {
    primary: '#2D3748',   // Warm dark grey
    secondary: '#718096', // Warm medium grey
    disabled: '#A0AEC0',  // Pearl grey
  },
  divider: '#E8E4DF',     // Warm beige divider
  grey: {
    50: '#FAFAF8',
    100: '#F7F5F0',
    200: '#EDE8E0',
    300: '#E2DED6',
    400: '#CBD5E0',
    500: '#A0AEC0',
    600: '#718096',
    700: '#4A5568',
    800: '#2D3748',
    900: '#1A202C',
  },
  // Prayer category colors (elegant pastels)
  prayer: {
    health: '#68D391',    // Sage green
    family: '#63B3ED',    // Sky blue
    work: '#F6AD55',      // Peach orange
    general: '#A0AEC0',   // Pearl grey
    other: '#D4A574',     // Warm gold
  },
  // Circle status colors
  circle: {
    public: '#68D391',    // Sage green
    private: '#D4A574',   // Warm gold
  },
  // Spiritual accents
  spiritual: {
    gold: '#D4A574',
    lightGold: '#E5C9A8',
    warmBeige: '#C9A87C',
    softCream: '#F7F5F0',
  },
};

// ==================== FOCUS INDICATORS ====================
const focusStyle = {
  outline: '3px solid rgba(44, 82, 130, 0.5)',
  outlineOffset: '2px',
  boxShadow: '0 0 0 3px rgba(44, 82, 130, 0.2)',
};

// ==================== BREAKPOINTS ====================
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// ==================== SPACING & SHAPE ====================
const spacing = 8;
const shape = {
  borderRadius: 16,  // Softer, more elegant corners
};

// ==================== ELEGANT SHADOWS ====================
// Softer, more diffuse shadows for a calm atmosphere
const shadows = [
  'none',
  '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
  '0 2px 6px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.04)',
  '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
  '0 6px 16px rgba(0, 0, 0, 0.10), 0 3px 8px rgba(0, 0, 0, 0.05)',
  '0 8px 20px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.06)',
  '0 10px 24px rgba(0, 0, 0, 0.14), 0 5px 12px rgba(0, 0, 0, 0.07)',
  '0 12px 28px rgba(0, 0, 0, 0.14), 0 6px 14px rgba(0, 0, 0, 0.08)',
  '0 14px 32px rgba(0, 0, 0, 0.15), 0 7px 16px rgba(0, 0, 0, 0.08)',
  '0 16px 36px rgba(0, 0, 0, 0.16), 0 8px 18px rgba(0, 0, 0, 0.09)',
  '0 18px 40px rgba(0, 0, 0, 0.16), 0 9px 20px rgba(0, 0, 0, 0.09)',
  '0 20px 44px rgba(0, 0, 0, 0.17), 0 10px 22px rgba(0, 0, 0, 0.10)',
  '0 22px 48px rgba(0, 0, 0, 0.18), 0 11px 24px rgba(0, 0, 0, 0.10)',
  '0 24px 52px rgba(0, 0, 0, 0.18), 0 12px 26px rgba(0, 0, 0, 0.11)',
  '0 26px 56px rgba(0, 0, 0, 0.19), 0 13px 28px rgba(0, 0, 0, 0.11)',
  '0 28px 60px rgba(0, 0, 0, 0.19), 0 14px 30px rgba(0, 0, 0, 0.12)',
  '0 30px 64px rgba(0, 0, 0, 0.20), 0 15px 32px rgba(0, 0, 0, 0.12)',
  '0 32px 68px rgba(0, 0, 0, 0.20), 0 16px 34px rgba(0, 0, 0, 0.13)',
  '0 34px 72px rgba(0, 0, 0, 0.21), 0 17px 36px rgba(0, 0, 0, 0.13)',
  '0 36px 76px rgba(0, 0, 0, 0.21), 0 18px 38px rgba(0, 0, 0, 0.14)',
  '0 38px 80px rgba(0, 0, 0, 0.22), 0 19px 40px rgba(0, 0, 0, 0.14)',
  '0 40px 84px rgba(0, 0, 0, 0.22), 0 20px 42px rgba(0, 0, 0, 0.15)',
  '0 42px 88px rgba(0, 0, 0, 0.23), 0 21px 44px rgba(0, 0, 0, 0.15)',
  '0 44px 92px rgba(0, 0, 0, 0.23), 0 22px 46px rgba(0, 0, 0, 0.16)',
  '0 46px 96px rgba(0, 0, 0, 0.24), 0 23px 48px rgba(0, 0, 0, 0.16)',
];

// ==================== TRANSITIONS ====================
const transitions = {
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    elegant: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',  // Elegant ease
  },
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
    elegant: 400,  // Slower, more elegant transitions
  },
};

// ==================== TYPOGRAPHY ====================
// Elegant typography with Playfair Display for headings
const typography = {
  fontFamily: '"Inter", "Source Sans Pro", "Roboto", sans-serif',
  fontFamilyHeading: '"Playfair Display", "Georgia", serif',
  fontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 600,
  h1: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    fontSize: '3rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: '#2D3748',
  },
  h2: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: '#2D3748',
  },
  h3: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    fontSize: '2rem',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0em',
    color: '#2D3748',
  },
  h4: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    color: '#2D3748',
  },
  h5: {
    fontFamily: '"Inter", "Source Sans Pro", sans-serif',
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.6,
    letterSpacing: '0em',
    color: '#2D3748',
  },
  h6: {
    fontFamily: '"Inter", "Source Sans Pro", sans-serif',
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
    color: '#2D3748',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.01em',
    color: '#4A5568',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.01em',
    color: '#4A5568',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.01em',
    color: '#2D3748',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.7,
    letterSpacing: '0.01em',
    color: '#4A5568',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03em',
    color: '#718096',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 2.66,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#718096',
  },
  // Special prayer typography
  prayer: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    fontStyle: 'italic',
    fontSize: '1.1rem',
    lineHeight: 1.8,
    color: '#2D3748',
  },
};

// ==================== COMPONENT OVERRIDES ====================
const components = {
  // Global CSS baseline
  MuiCssBaseline: {
    styleOverrides: {
      // Import Google Fonts
      '@import': [
        "url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap')",
        "url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap')",
      ].join(';'),
      body: {
        backgroundColor: '#FAFAF8',
        scrollBehavior: 'smooth',
      },
    },
  },

  // ===== BUTTONS =====
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        padding: '10px 24px',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: 'none',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '&:focus-visible': focusStyle,
      },
      contained: {
        boxShadow: '0 2px 8px rgba(44, 82, 130, 0.15)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(44, 82, 130, 0.25)',
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: '0 1px 4px rgba(44, 82, 130, 0.15)',
        },
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #2C5282 0%, #1A365D 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #3A6AA8 0%, #2C5282 100%)',
        },
      },
      containedSecondary: {
        background: 'linear-gradient(135deg, #D4A574 0%, #B8844D 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #E5C9A8 0%, #D4A574 100%)',
        },
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
          backgroundColor: 'rgba(44, 82, 130, 0.04)',
        },
      },
      outlinedPrimary: {
        borderColor: '#2C5282',
        '&:hover': {
          borderColor: '#1A365D',
          backgroundColor: 'rgba(44, 82, 130, 0.06)',
        },
      },
      text: {
        '&:hover': {
          backgroundColor: 'rgba(44, 82, 130, 0.04)',
        },
      },
      sizeLarge: {
        padding: '14px 32px',
        fontSize: '1rem',
        borderRadius: 14,
      },
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.8125rem',
        borderRadius: 10,
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '&:focus-visible': focusStyle,
        '&:hover': {
          backgroundColor: 'rgba(44, 82, 130, 0.08)',
        },
      },
    },
  },

  // ===== FAB =====
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: '0 4px 20px rgba(44, 82, 130, 0.25)',
        '&:hover': {
          boxShadow: '0 6px 28px rgba(44, 82, 130, 0.35)',
        },
      },
      primary: {
        background: 'linear-gradient(135deg, #2C5282 0%, #1A365D 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #3A6AA8 0%, #2C5282 100%)',
        },
      },
      secondary: {
        background: 'linear-gradient(135deg, #D4A574 0%, #B8844D 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #E5C9A8 0%, #D4A574 100%)',
        },
      },
    },
  },

  // ===== INPUTS =====
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          '&:focus-within': {
            boxShadow: '0 0 0 3px rgba(44, 82, 130, 0.15)',
          },
          '& fieldset': {
            borderColor: '#E2DED6',
            borderWidth: '1.5px',
          },
          '&:hover fieldset': {
            borderColor: '#CBD5E0',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#2C5282',
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#718096',
          '&.Mui-focused': {
            color: '#2C5282',
          },
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: '#CBD5E0',
        '&.Mui-checked': {
          color: '#2C5282',
        },
        '&:focus-visible': focusStyle,
      },
    },
  },

  // ===== NAVIGATION =====
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontSize: '0.9375rem',
        fontWeight: 500,
        minHeight: 48,
        color: '#718096',
        '&.Mui-selected': {
          color: '#2C5282',
          fontWeight: 600,
        },
        '&:focus-visible': focusStyle,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: '#2C5282',
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
  },

  MuiBottomNavigation: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #E8E4DF',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.06)',
      },
    },
  },

  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        color: '#718096',
        '&.Mui-selected': {
          color: '#2C5282',
        },
        minWidth: 'auto',
        padding: '8px 12px',
      },
      label: {
        fontSize: '0.75rem',
        '&.Mui-selected': {
          fontSize: '0.75rem',
          fontWeight: 600,
        },
      },
    },
  },

  // ===== SURFACES =====
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(232, 228, 223, 0.8)',
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '20px 24px',
        '&:last-child': {
          paddingBottom: 20,
        },
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 16,
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(232, 228, 223, 0.6)',
      },
      elevation2: {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(232, 228, 223, 0.5)',
      },
      elevation3: {
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.08)',
      },
      elevation4: {
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.10)',
      },
    },
  },

  // ===== DIALOGS & MODALS =====
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 24,
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontSize: '1.5rem',
        fontWeight: 500,
        padding: '24px 28px 12px',
        color: '#2D3748',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 28px',
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 28px 24px',
        gap: 12,
      },
    },
  },

  // ===== FEEDBACK =====
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        padding: '14px 20px',
        border: '1px solid',
      },
      standardSuccess: {
        backgroundColor: 'rgba(104, 211, 145, 0.12)',
        borderColor: 'rgba(104, 211, 145, 0.3)',
        '& .MuiAlert-icon': {
          color: '#48BB78',
        },
        '& .MuiAlert-message': {
          color: '#276749',
        },
      },
      standardInfo: {
        backgroundColor: 'rgba(99, 179, 237, 0.12)',
        borderColor: 'rgba(99, 179, 237, 0.3)',
        '& .MuiAlert-icon': {
          color: '#4299E1',
        },
        '& .MuiAlert-message': {
          color: '#2B6CB0',
        },
      },
      standardWarning: {
        backgroundColor: 'rgba(246, 173, 85, 0.12)',
        borderColor: 'rgba(246, 173, 85, 0.3)',
        '& .MuiAlert-icon': {
          color: '#DD6B20',
        },
        '& .MuiAlert-message': {
          color: '#9C4221',
        },
      },
      standardError: {
        backgroundColor: 'rgba(229, 62, 62, 0.12)',
        borderColor: 'rgba(229, 62, 62, 0.3)',
        '& .MuiAlert-icon': {
          color: '#E53E3E',
        },
        '& .MuiAlert-message': {
          color: '#C53030',
        },
      },
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      root: {
        '& .MuiSnackbarContent-root': {
          borderRadius: 14,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },

  // ===== DATA DISPLAY =====
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
      filled: {
        backgroundColor: '#F7F5F0',
        '&:hover': {
          backgroundColor: '#EDE8E0',
        },
      },
      outlined: {
        borderColor: '#E2DED6',
        '&:hover': {
          backgroundColor: 'rgba(44, 82, 130, 0.04)',
        },
      },
      colorPrimary: {
        backgroundColor: 'rgba(44, 82, 130, 0.12)',
        color: '#2C5282',
      },
      colorSecondary: {
        backgroundColor: 'rgba(212, 165, 116, 0.2)',
        color: '#8B5A2B',
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        backgroundColor: '#2C5282',
        color: '#FFFFFF',
        fontWeight: 500,
      },
      colorDefault: {
        backgroundColor: '#E2DED6',
        color: '#4A5568',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#2D3748',
        fontSize: '0.8125rem',
        padding: '10px 16px',
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
      },
      arrow: {
        color: '#2D3748',
      },
    },
  },

  MuiBadge: {
    styleOverrides: {
      badge: {
        fontWeight: 600,
        fontSize: '0.7rem',
      },
      colorPrimary: {
        backgroundColor: '#2C5282',
      },
      colorSecondary: {
        backgroundColor: '#D4A574',
      },
      colorError: {
        backgroundColor: '#E53E3E',
      },
    },
  },

  // ===== LISTS =====
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        '&.Mui-selected': {
          backgroundColor: 'rgba(44, 82, 130, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(44, 82, 130, 0.12)',
          },
        },
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(44, 82, 130, 0.06)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(44, 82, 130, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(44, 82, 130, 0.12)',
          },
        },
      },
    },
  },

  // ===== LAYOUT =====
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        backgroundColor: '#FFFFFF',
        borderRight: '1px solid #E8E4DF',
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 8px rgba(0, 0, 0, 0.05)',
        borderBottom: '1px solid rgba(232, 228, 223, 0.8)',
      },
      colorPrimary: {
        backgroundColor: '#FFFFFF',
        color: '#2D3748',
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: '#E8E4DF',
      },
    },
  },

  // ===== SKELETON =====
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: '#F7F5F0',
      },
      wave: {
        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
        },
      },
    },
  },
};

// ==================== CREATE LIGHT THEME ====================
export const lightTheme = createTheme({
  palette: colors,
  breakpoints,
  spacing,
  shape,
  typography,
  shadows,
  transitions,
  components,
});

// ==================== CREATE DARK THEME ====================
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#63B3ED',
      light: '#90CDF4',
      dark: '#4299E1',
      contrastText: '#1A202C',
    },
    secondary: {
      main: '#E5C9A8',
      light: '#F0DCC4',
      dark: '#D4A574',
      contrastText: '#1A202C',
    },
    success: {
      main: '#68D391',
      light: '#9AE6B4',
      dark: '#48BB78',
      contrastText: '#1A202C',
    },
    warning: {
      main: '#F6AD55',
      light: '#FBD38D',
      dark: '#ED8936',
      contrastText: '#1A202C',
    },
    error: {
      main: '#FC8181',
      light: '#FEB2B2',
      dark: '#E53E3E',
      contrastText: '#1A202C',
    },
    info: {
      main: '#63B3ED',
      light: '#90CDF4',
      dark: '#4299E1',
      contrastText: '#1A202C',
    },
    background: {
      default: '#1A202C',
      paper: '#2D3748',
      warm: '#2D3748',
    },
    text: {
      primary: '#F7FAFC',
      secondary: '#CBD5E0',
      disabled: '#718096',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: '#F7FAFC',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    prayer: {
      health: '#68D391',
      family: '#63B3ED',
      work: '#F6AD55',
      general: '#A0AEC0',
      other: '#E5C9A8',
    },
    spiritual: {
      gold: '#E5C9A8',
      lightGold: '#F0DCC4',
      warmBeige: '#D4A574',
      softCream: '#4A5568',
    },
  },
  breakpoints,
  spacing,
  shape,
  typography: {
    ...typography,
    h1: { ...typography.h1, color: '#F7FAFC' },
    h2: { ...typography.h2, color: '#F7FAFC' },
    h3: { ...typography.h3, color: '#F7FAFC' },
    h4: { ...typography.h4, color: '#F7FAFC' },
    h5: { ...typography.h5, color: '#F7FAFC' },
    h6: { ...typography.h6, color: '#F7FAFC' },
    body1: { ...typography.body1, color: '#F7FAFC' },
    body2: { ...typography.body2, color: '#CBD5E0' },
    caption: { ...typography.caption, color: '#A0AEC0' },
  },
  shadows,
  transitions,
  components: {
    ...components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#1A202C',
          scrollBehavior: 'smooth',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#2D3748',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D3748',
          borderColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#2D3748',
          color: '#F7FAFC',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1A202C',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
    },
  },
});

// Default export
export default lightTheme;

// Export color utilities for use in components
export const prayerCategoryColors = {
  Health: colors.prayer.health,
  Family: colors.prayer.family,
  Work: colors.prayer.work,
  General: colors.prayer.general,
  Other: colors.prayer.other,
};

export const getCategoryColor = (category) => {
  return prayerCategoryColors[category] || colors.prayer.general;
};
